import classNames from "classnames";
import React from "react";
import { ITableEditorAlert } from "./table-editor-errors";

export interface ITableEditorAlertAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ITableEditorAlertProps {
  alert: ITableEditorAlert;
  actions?: ITableEditorAlertAction[];
  className?: string;
  onDismiss?: () => void;
  // Test hook. Several alerts can be mounted at once (load + save +
  // import notice), so the id identifies WHICH alert this is; the
  // title / message ids below are only addressable scoped inside it.
  testId?: string;
}

// Inline SVG glyphs keyed by tone. Rendered text inside the icon pill
// reads as a developer placeholder ("OK" / "!" / "i" looks unfinished),
// and a stroke-only SVG scales cleanly across the tone colours via
// `currentColor`. 14×14 viewbox matches the icon pill's interior.
const ToneIcon: React.FC<{ tone: ITableEditorAlert["tone"] }> = ({ tone }): JSX.Element => {
  if (tone === "success") {
    // Check mark.
    return (
      <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
        <path
          d="M3 7.5l2.6 2.5L11 4.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (tone === "warn") {
    // Warning triangle with exclamation. Asymmetric apex avoids the
    // "info circle" silhouette the error glyph uses.
    return (
      <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
        <path
          d="M7 1.7l5.6 9.4a.7.7 0 01-.6 1H2a.7.7 0 01-.6-1L7 1.7z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M7 5.4v3.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="7" cy="10.4" r="0.8" fill="currentColor" />
      </svg>
    );
  }
  if (tone === "error") {
    // Circle with X — recognisable error mark independent of tone colour
    // for colour-blind users (red surface is reinforcing, not load-bearing).
    return (
      <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="5.6" stroke="currentColor" strokeWidth="1.4" />
        <path d="M4.7 4.7l4.6 4.6M9.3 4.7l-4.6 4.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  // Info — circle with lowercase i.
  return (
    <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.6" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="7" cy="4.3" r="0.85" fill="currentColor" />
      <path d="M7 6.4v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
};

// Crisp × glyph for the dismiss button. The string "x" reads as a
// lowercase letter at small sizes; an SVG stroke pair is unambiguous.
const DismissIcon: React.FC = (): JSX.Element => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden="true">
    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const TableEditorAlert: React.FC<ITableEditorAlertProps> = (props: ITableEditorAlertProps): JSX.Element => {
  const { alert, actions = [], className, onDismiss, testId } = props;
  const hasNextSteps: boolean = alert.nextSteps.length > 0;
  const hasDetails: boolean = alert.details.length > 0 || alert.technicalDetails !== undefined;
  const role: "alert" | "status" = alert.tone === "error" || alert.tone === "warn" ? "alert" : "status";
  const classes: string = classNames(
    "kuika-table-editor__alert",
    `kuika-table-editor__alert--${alert.tone}`,
    className
  );

  return (
    <div className={classes} role={role} data-testid={testId}>
      <span className="kuika-table-editor__alert-icon" aria-hidden="true">
        <ToneIcon tone={alert.tone} />
      </span>
      <div className="kuika-table-editor__alert-body">
        <div className="kuika-table-editor__alert-heading">
          <strong className="kuika-table-editor__alert-title" data-testid="tableEditorAlertTitle">
            {alert.title}
          </strong>
          {alert.status !== undefined && <span className="kuika-table-editor__alert-meta">HTTP {alert.status}</span>}
        </div>
        <div className="kuika-table-editor__alert-message" data-testid="tableEditorAlertMessage">
          {alert.message}
        </div>
        {hasNextSteps && (
          <ul className="kuika-table-editor__alert-steps">
            {alert.nextSteps.map((step: string, index: number): JSX.Element => (
              <li key={`${step}-${index}`}>{step}</li>
            ))}
          </ul>
        )}
        {hasDetails && (
          <details className="kuika-table-editor__alert-details">
            <summary>Details</summary>
            {alert.details.length > 0 && (
              <ul className="kuika-table-editor__alert-detail-list">
                {alert.details.map((detail: string, index: number): JSX.Element => (
                  <li key={`${detail}-${index}`}>{detail}</li>
                ))}
              </ul>
            )}
            {alert.technicalDetails !== undefined && (
              <code className="kuika-table-editor__alert-tech">{alert.technicalDetails}</code>
            )}
          </details>
        )}
      </div>
      {actions.length > 0 && (
        <div className="kuika-table-editor__alert-actions">
          {actions.map((action: ITableEditorAlertAction): JSX.Element => (
            <button
              key={action.label}
              type="button"
              className="kuika-table-editor__alert-action"
              disabled={action.disabled}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      {onDismiss && (
        <button type="button" className="kuika-table-editor__alert-dismiss" onClick={onDismiss} aria-label="Dismiss">
          <DismissIcon />
        </button>
      )}
    </div>
  );
};

export default TableEditorAlert;
